import { useState } from 'react';
import { Alert, Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { api, type Link, type LinkType } from '../api';

const TYPES: LinkType[] = ['blocks', 'blocked-by', 'relates-to'];

function LinkRow({ link, onRemove }: { link: Link; onRemove: () => void }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ minWidth: 92, color: 'text.secondary' }}>
        {link.type}
      </Typography>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        DWLF-{link.ref}
      </Typography>
      <IconButton size="small" onClick={onRemove} aria-label={`Remove ${link.type} DWLF-${link.ref}`}>
        <CloseIcon fontSize="inherit" />
      </IconButton>
    </Stack>
  );
}

/**
 * Links are what turn an ordering constraint from prose into data — the backlog's
 * DWLF-87 → 83 → 85 → 84 → 81 chain being the case in point. Both directions are written
 * server-side, so adding "blocks" here makes "blocked-by" appear on the other ticket.
 */
export function Links({
  ticketRef,
  links,
  onChanged,
}: {
  ticketRef: number;
  links: Link[];
  onChanged: () => void;
}) {
  const [type, setType] = useState<LinkType>('blocks');
  const [other, setOther] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setProblem(null);
    try {
      await action();
      onChanged();
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'Link operation failed.');
    } finally {
      setBusy(false);
    }
  }

  const target = Number(other.replace(/^DWLF-/i, ''));
  const valid = Number.isInteger(target) && target > 0 && target !== ticketRef;

  return (
    <Stack spacing={1.5}>
      <Typography variant="overline" color="text.secondary">
        Links {links.length > 0 && `(${links.length})`}
      </Typography>

      {links.map((link) => (
        <LinkRow
          key={`${link.type}-${link.ref}`}
          link={link}
          onRemove={() => void run(() => api.unlink(ticketRef, link.type, link.ref))}
        />
      ))}

      {problem && (
        <Alert severity="error" onClose={() => setProblem(null)}>
          {problem}
        </Alert>
      )}

      <AddLink
        state={{ type, other, busy, valid }}
        onChange={(next) => {
          setType(next.type);
          setOther(next.other);
        }}
        onSubmit={() =>
          void run(async () => {
            await api.link(ticketRef, type, target);
            setOther('');
          })
        }
      />
    </Stack>
  );
}

function AddLink({
  state,
  onChange,
  onSubmit,
}: {
  state: { type: LinkType; other: string; busy: boolean; valid: boolean };
  onChange: (next: { type: LinkType; other: string }) => void;
  onSubmit: () => void;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        select
        size="small"
        value={state.type}
        onChange={(event) => onChange({ ...state, type: event.target.value as LinkType })}
        sx={{ minWidth: 130 }}
      >
        {TYPES.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        placeholder="DWLF-83"
        value={state.other}
        onChange={(event) => onChange({ ...state, other: event.target.value })}
        sx={{ width: 120 }}
      />
      <Box>
        <Button size="small" variant="outlined" disabled={state.busy || !state.valid} onClick={onSubmit}>
          Link
        </Button>
      </Box>
    </Stack>
  );
}
